// Small pieces the two SMS-number screens share (Admin → Outreach and the Practice
// Details override card): the dismissable message, the status badge + dry-run line,
// and the read-only webhook URL list with copy buttons. No patient data anywhere.

import React, { useEffect, useState } from "react";

import { DRY_RUN_LINE, WEBHOOK_PATHS, smsStatusLine } from "./smsNumberApi";
import "./SmsNumberCard.css";

export function SmsMessage({ tone, children, onDismiss }) {
  if (!children) return null;
  return (
    <div className={`sms-message sms-message-${tone}`} role={tone === "error" ? "alert" : "status"}>
      <span>{children}</span>
      {onDismiss && (
        <button type="button" className="sms-message-close" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      )}
    </div>
  );
}

// `number` is the number a text would go out from; `noNumberReason` is the
// screen's wording for a missing one.
export function SmsStatus({ status, number, noNumberReason }) {
  const line = smsStatusLine({ ...status, number }, noNumberReason);
  return (
    <>
      <div className="sms-status">
        <span className={`sms-badge ${line.ok ? "sms-badge-ok" : "sms-badge-off"}`}>
          {line.ok ? "SMS configured" : "SMS not configured"}
        </span>
        {!line.ok && <span className="sms-muted">{line.text.replace(/^SMS not configured:?\s*/, "")}</span>}
      </div>
      {status.dryRun && <p className="sms-dry-run">{DRY_RUN_LINE}</p>}
    </>
  );
}

function CopyButton({ text }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!state) return undefined;
    const t = setTimeout(() => setState(null), 2000);
    return () => clearTimeout(t);
  }, [state]);

  const copy = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error("no clipboard");
      await navigator.clipboard.writeText(text);
      setState("Copied");
    } catch (err) {
      setState("Select and copy");
    }
  };

  return (
    <button type="button" className="sms-btn sms-btn-small" onClick={copy} aria-label={`Copy ${text}`}>
      {state || "Copy"}
    </button>
  );
}

const WEBHOOKS = [
  { key: "inboundSms", name: "Inbound SMS" },
  { key: "statusCallback", name: "Status callback" },
];

// The two URLs a Twilio Messaging Service must point at. When the server does not
// know its public URL it sends null and we show the path with a note.
export function WebhookList({ webhooks }) {
  return (
    <>
      <h4 className="sms-subhead">Twilio webhooks</h4>
      <p className="sms-intro">
        Point the Twilio Messaging Service at these two URLs so replies and delivery
        receipts reach Atlas.
      </p>
      <ul className="sms-webhooks">
        {WEBHOOKS.map(({ key, name }) => {
          const url = webhooks && webhooks[key];
          const shown = url || WEBHOOK_PATHS[key];
          return (
            <li key={key} className="sms-webhook">
              <span className="sms-webhook-name">{name}</span>
              <code className="sms-webhook-url">{shown}</code>
              <CopyButton text={shown} />
              {!url && (
                <span className="sms-webhook-note">
                  Path only — set ATLAS_PUBLIC_URL on the server to see the full URL.
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}
